import { useState, useEffect } from 'react';
import { Settings2, Bot, Users, UserCheck, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useUpdateReview } from '@/features/review/hooks/useReview';
import { ExclusionReasonConfig } from './ExclusionReasonConfig';
import type {
  Review,
  ReviewMode,
  ReviewSettings as ReviewSettingsType,
} from '@/features/review/types';
import { DEFAULT_REVIEW_SETTINGS } from '@/features/review/types';

interface ReviewSettingsProps {
  review: Review;
  className?: string;
}

const REVIEW_MODES: Array<{
  value: ReviewMode;
  label: string;
  description: string;
  icon: typeof Bot;
}> = [
  {
    value: 'ai_only',
    label: 'AI Only',
    description: 'AI evaluates all sources automatically. No human reviewer required.',
    icon: Bot,
  },
  {
    value: 'ai_human',
    label: 'AI + Human',
    description: 'AI screens first, human confirms or overrides. Best for speed with oversight.',
    icon: UserCheck,
  },
  {
    value: 'dual_human',
    label: 'Dual Human',
    description: 'Two independent human reviewers per source. Gold standard for systematic reviews.',
    icon: Users,
  },
  {
    value: 'full',
    label: 'Full (AI + Dual)',
    description: 'AI pre-screens, then two human reviewers confirm. Maximum rigor.',
    icon: ShieldCheck,
  },
];

export function ReviewSettings({ review, className }: ReviewSettingsProps) {
  const updateReview = useUpdateReview();
  const settings: ReviewSettingsType = {
    ...DEFAULT_REVIEW_SETTINGS,
    ...(review.settings as Partial<ReviewSettingsType>),
  };

  const [mode, setMode] = useState<ReviewMode>(review.review_mode);
  const [blinded, setBlinded] = useState(review.blinded);
  const [includeThreshold, setIncludeThreshold] = useState(settings.include_threshold);
  const [excludeThreshold, setExcludeThreshold] = useState(settings.exclude_threshold);
  const [exclusionReasons, setExclusionReasons] = useState(settings.exclusion_reasons);
  const [requireReason, setRequireReason] = useState(settings.require_exclusion_reason);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDirty(
      mode !== review.review_mode ||
      blinded !== review.blinded ||
      includeThreshold !== settings.include_threshold ||
      excludeThreshold !== settings.exclude_threshold ||
      JSON.stringify(exclusionReasons) !== JSON.stringify(settings.exclusion_reasons) ||
      requireReason !== settings.require_exclusion_reason,
    );
  }, [mode, blinded, includeThreshold, excludeThreshold, exclusionReasons, requireReason, review, settings]);

  const handleSave = async () => {
    try {
      await updateReview.mutateAsync({
        reviewId: review.id,
        blinded,
        review_mode: mode,
        settings: {
          include_threshold: includeThreshold,
          exclude_threshold: excludeThreshold,
          exclusion_reasons: exclusionReasons,
          require_exclusion_reason: requireReason,
          screening_order: settings.screening_order,
        },
      });
      setDirty(false);
    } catch {
      toast.error('Failed to save settings');
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Review Mode */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-indigo-500" />
            <CardTitle className="text-base">Review Mode</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {REVIEW_MODES.map((rm) => {
              const Icon = rm.icon;
              const selected = mode === rm.value;
              return (
                <button
                  key={rm.value}
                  type="button"
                  onClick={() => setMode(rm.value)}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border text-left transition-all',
                    selected
                      ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                      : 'border-slate-200 hover:border-slate-300',
                  )}
                >
                  <Icon
                    className={cn(
                      'h-5 w-5 mt-0.5 shrink-0',
                      selected ? 'text-indigo-600' : 'text-slate-400',
                    )}
                  />
                  <div>
                    <p className={cn('text-sm font-medium', selected ? 'text-indigo-900' : 'text-slate-700')}>
                      {rm.label}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">{rm.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Blinded & Thresholds */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Screening Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Blinded toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Blinded Review</Label>
              <p className="text-xs text-slate-500">Reviewers cannot see each other's decisions</p>
            </div>
            <Switch checked={blinded} onCheckedChange={setBlinded} />
          </div>

          <Separator />

          {/* Require exclusion reason */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Require Exclusion Reason</Label>
              <p className="text-xs text-slate-500">Reviewers must select a reason when excluding</p>
            </div>
            <Switch checked={requireReason} onCheckedChange={setRequireReason} />
          </div>

          <Separator />

          {/* Include threshold */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Include Threshold</Label>
              <Badge variant="outline" className="text-xs">{includeThreshold}%</Badge>
            </div>
            <Slider
              value={[includeThreshold]}
              onValueChange={([v]) => setIncludeThreshold(v ?? 0)}
              min={0}
              max={100}
              step={5}
            />
            <p className="text-xs text-slate-500">
              AI scores at or above this will be auto-included
            </p>
          </div>

          {/* Exclude threshold */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Exclude Threshold</Label>
              <Badge variant="outline" className="text-xs">{excludeThreshold}%</Badge>
            </div>
            <Slider
              value={[excludeThreshold]}
              onValueChange={([v]) => setExcludeThreshold(v ?? 0)}
              min={0}
              max={100}
              step={5}
            />
            <p className="text-xs text-slate-500">
              AI scores below this will be auto-excluded
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Exclusion Reasons */}
      <ExclusionReasonConfig
        reasons={exclusionReasons}
        onChange={setExclusionReasons}
      />

      {/* Save button */}
      {dirty && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={updateReview.isPending}>
            {updateReview.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      )}
    </div>
  );
}
